/*
  Klaviyo popup — custom trigger

  Replaces Klaviyo's built-in EXIT_INTENT trigger, which on mobile fires on any
  fast upward scroll (> 50px per frame) — including the theme's own scroll back
  to an invalid field when personalized-form validation fails — and on desktop
  fires when the cursor leaves any edge of the window.

  The Klaviyo form must be set to open via custom JavaScript trigger. With that trigger
  Klaviyo ignores every other targeting rule on the form, so this script applies them:
  existing profiles, the re-show cooldown and the excluded path.

  The form opens once per page view when ALL of these hold:
    - not on the excluded path (/cart), not within the cooldown since it last opened/closed
    - the visitor is not an identified Klaviyo profile (klaviyo.isIdentified, or the
      __kla_id cookie when Klaviyo does not answer within IDENTIFY_TIMEOUT_MS)
    - no personalized form (Customall / Customily / ShineOn) is in the viewport
    - the shopper is not busy: tab visible, no field focused, no modal/drawer open
    - and one of:
        idle >= data-idle-ms (all devices; timer restarts when the tab becomes visible)
        cursor leaves through the top edge (mouse devices only) with no form
        interaction in the last data-form-quiet-ms

  QA debug log in the browser console, prefixed [klaviyo-popup] (per tab, persists across navigation):
    ?popup_debug=1      log trigger status + Klaviyo form events
    ?popup_debug=reset  same, and clear the cooldown so the popup can show again
    ?popup_debug=0      turn it off
*/
(function () {
  var script = document.currentScript;
  var formId = script && script.dataset.formId;
  if (!formId) return;

  var IDLE_MS = Number(script.dataset.idleMs) || 15000;
  var FORM_QUIET_MS = Number(script.dataset.formQuietMs) || 10000;
  var COOLDOWN_MS = (Number(script.dataset.cooldownDays) || 5) * 86400000;
  var EXCLUDED_PATH = script.dataset.excludePath || '/cart';
  var STORAGE_KEY = 'klaviyoPopupTrigger:' + formId;
  var DEBUG_KEY = 'klaviyoPopupTrigger:debug';
  var TICK_MS = 1000;
  var IDENTIFY_TIMEOUT_MS = 3000;
  // Keys Klaviyo's tracker treats as identifying (any one present in __kla_id = identified)
  var IDENTITY_KEYS = ['$exchange_id', 'id', '$email', '$id', '$kid', '$anonymous', '$phone_number', 'email', 'phone_number'];

  var FORM_SELECTORS = '#personalized-form, #cl_optionsapp, #customily-options, #so-options-form, .so-product-form';
  var FIELD_SELECTORS = 'input, textarea, select, [contenteditable="true"]';
  var OVERLAY_SELECTORS = '.fancybox-container, [aria-modal="true"]';
  var ACTIVITY_EVENTS = ['scroll', 'wheel', 'touchstart', 'touchmove', 'pointerdown', 'mousemove', 'keydown', 'input'];
  var FORM_INTERACTION_EVENTS = ['pointerdown', 'focusin', 'input', 'change'];

  var hasMouse = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var listenerOpts = { capture: true, passive: true };
  var lastActivity = Date.now();
  var lastFormInteraction = 0;
  var opened = false;
  var openReason = '';
  var timer = null;

  /* ---------- QA debug log ---------- */

  var debug = initDebug();
  var lastLog = '';

  function initDebug() {
    var param = new URLSearchParams(window.location.search).get('popup_debug');
    try {
      if (param === '0') sessionStorage.removeItem(DEBUG_KEY);
      else if (param) sessionStorage.setItem(DEBUG_KEY, '1');
      if (param === 'reset') localStorage.removeItem(STORAGE_KEY);
      return sessionStorage.getItem(DEBUG_KEY) === '1';
    } catch (e) {
      return !!param && param !== '0';
    }
  }

  // Repeated lines are skipped so the 1s tick does not flood the console
  function log(text) {
    if (!debug || text === lastLog) return;
    lastLog = text;
    console.info('[klaviyo-popup]', text);
  }

  /* ---------- Conditions ---------- */

  function inViewport(el) {
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function anyInViewport(selectors) {
    var els = document.querySelectorAll(selectors);
    for (var i = 0; i < els.length; i++) {
      if (inViewport(els[i])) return true;
    }
    return false;
  }

  function scrollLocked(el) {
    var style = getComputedStyle(el);
    return style.overflowY === 'hidden' || style.overflow === 'hidden';
  }

  // Modals, drawers and Fancybox lock page scroll (body.fixed, body.blocked-scroll, ...)
  function overlayOpen() {
    if (scrollLocked(document.body) || scrollLocked(document.documentElement)) return true;
    return anyInViewport(OVERLAY_SELECTORS);
  }

  // Empty string when the popup may open; otherwise why it may not (logged in debug mode)
  function blockReason() {
    if (opened) return 'already opened';
    // personalized-v4 hides Klaviyo popups via CSS; opening one would only lock scroll
    if (document.documentElement.classList.contains('pv2-active')) return 'personalized-v4 template';
    if (document.hidden) return 'tab hidden';
    var active = document.activeElement;
    if (active && active.matches && active.matches(FIELD_SELECTORS)) return 'input focused';
    if (overlayOpen()) return 'modal/drawer open';
    if (anyInViewport(FORM_SELECTORS)) return 'personalized form in viewport';
    return '';
  }

  function inCooldown() {
    try {
      var last = Number(localStorage.getItem(STORAGE_KEY));
      return last > 0 && Date.now() - last < COOLDOWN_MS ? last : 0;
    } catch (e) {
      return 0;
    }
  }

  function cookieIdentified() {
    var match = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]*)/);
    if (!match) return false;
    try {
      var identity = JSON.parse(atob(decodeURIComponent(match[1])));
      return IDENTITY_KEYS.some(function (key) { return !!identity[key]; });
    } catch (e) {
      return false;
    }
  }

  // klaviyo.isIdentified() is answered by Klaviyo's tracking script (static-tracking.klaviyo.com).
  // When that script is blocked the promise never settles (TMZ-1107), so after IDENTIFY_TIMEOUT_MS
  // the __kla_id cookie decides and a later answer is ignored.
  // Klaviyo missing or erroring counts as identified: better to skip than nag a subscriber.
  function isIdentified() {
    var klaviyo = window.klaviyo;
    if (!klaviyo || typeof klaviyo.isIdentified !== 'function') return Promise.resolve({ identified: true });
    var timeoutId;
    var answer = new Promise(function (resolve) {
      resolve(klaviyo.isIdentified());
    }).then(
      function (identified) {
        clearTimeout(timeoutId);
        return { identified: !!identified };
      },
      function () {
        clearTimeout(timeoutId);
        return { identified: true };
      }
    );
    var fallback = new Promise(function (resolve) {
      timeoutId = setTimeout(function () {
        resolve({ identified: cookieIdentified(), viaCookie: true });
      }, IDENTIFY_TIMEOUT_MS);
    });
    return Promise.race([answer, fallback]);
  }

  /* ---------- Triggers ---------- */

  function markActivity() {
    lastActivity = Date.now();
  }

  function markFormInteraction(e) {
    if (e.target && e.target.closest && e.target.closest(FORM_SELECTORS)) {
      lastFormInteraction = Date.now();
    }
  }

  function onVisibilityChange() {
    if (!document.hidden) markActivity();
  }

  function onMouseOut(e) {
    // Leaving the window through the top edge only (tabs / address bar)
    if (e.relatedTarget || e.clientY > 0) return;
    var reason = Date.now() - lastFormInteraction < FORM_QUIET_MS ? 'recent form interaction' : blockReason();
    if (reason) return log('top-edge exit blocked: ' + reason);
    open('top-edge exit');
  }

  function tick() {
    var idle = Date.now() - lastActivity;
    var reason = blockReason();
    if (idle >= IDLE_MS && !reason) return open('idle ' + IDLE_MS / 1000 + 's');
    log(reason ? 'blocked: ' + reason : 'waiting for idle ' + IDLE_MS / 1000 + 's');
  }

  function stop() {
    clearInterval(timer);
    ACTIVITY_EVENTS.forEach(function (type) {
      document.removeEventListener(type, markActivity, listenerOpts);
    });
    FORM_INTERACTION_EVENTS.forEach(function (type) {
      document.removeEventListener(type, markFormInteraction, listenerOpts);
    });
    document.removeEventListener('visibilitychange', onVisibilityChange);
    document.removeEventListener('mouseout', onMouseOut);
  }

  function open(reason) {
    opened = true;
    openReason = reason;
    stop();
    log('checking Klaviyo profile (' + reason + ')');
    isIdentified().then(function (result) {
      var source = result.viaCookie ? ', isIdentified timed out, used __kla_id cookie' : '';
      if (result.identified) return log('not opened: identified profile or Klaviyo not ready (' + reason + source + ')');
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', formId]);
      log('openForm ' + formId + ' (' + reason + source + ')');
    });
  }

  function onKlaviyoForm(e) {
    var detail = e.detail || {};
    if (!detail.formId || ['open', 'close', 'submit'].indexOf(detail.type) === -1) return;
    log('Klaviyo form ' + detail.formId + ' ' + detail.type + (detail.formId === formId && openReason ? ' (' + openReason + ')' : ''));
    if (detail.formId !== formId || detail.type === 'submit') return;
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (err) {}
  }

  /* ---------- Init ---------- */

  window.addEventListener('klaviyoForms', onKlaviyoForm);

  if (window.location.pathname.indexOf(EXCLUDED_PATH) === 0) return log('not running on ' + EXCLUDED_PATH);
  var cooldownSince = inCooldown();
  if (cooldownSince) {
    var hoursLeft = Math.ceil((cooldownSince + COOLDOWN_MS - Date.now()) / 3600000);
    return log('not running: cooldown, ' + hoursLeft + 'h left (?popup_debug=reset clears it)');
  }

  ACTIVITY_EVENTS.forEach(function (type) {
    document.addEventListener(type, markActivity, listenerOpts);
  });
  FORM_INTERACTION_EVENTS.forEach(function (type) {
    document.addEventListener(type, markFormInteraction, listenerOpts);
  });
  document.addEventListener('visibilitychange', onVisibilityChange);
  if (hasMouse) document.addEventListener('mouseout', onMouseOut);
  timer = setInterval(tick, TICK_MS);
})();
